import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAccess } from '../useAccess';
import STORYLINES from './storylinesDatabase';
import SuperstarPicker from './SuperstarPicker';

const LOG_CATS = {
  arena:       { label: 'Arena',    maxRank: 6, names: ['','High School Gym','Concert Hall','Wrestling Center','Classic Arena','Stadium','Grand Stadium'] },
  crew:        { label: 'Crew',     maxRank: 7, names: ['','Tour Crew','Backstage Crew','Gorilla Crew','Stage Crew','Expert Crew','Veteran Crew','Immortal Crew'] },
  sfx:         { label: 'SFX',      maxRank: 7, names: ['','Basic Effects','Stage Lighting','Pyro Entry','Stage Show','Expert FX','Veteran FX','Immortal FX'] },
  advertising: { label: 'Ads',      maxRank: 7, names: ['','Local Flyers','Social Media','Radio Spots','TV Spots','Expert Campaign','Veteran Campaign','Immortal Campaign'] },
};

const RIVALRY_LEVEL_LABELS = { 1: 'BUILDING', 2: 'HEATED', 3: 'INTENSE', 4: 'BLOOD FEUD' };

export default function AdminDevPanel({ gameData, onSave, onClose, onShowHofSequence }) {
  const [user] = useAuthState(auth);
  const accessStatus = useAccess(user, false);

  const [tokens, setTokens]     = useState(gameData?.logisticsTokens ?? 0);
  const [budget, setBudget]     = useState(gameData?.budget ?? 0);
  const [fans, setFans]         = useState(gameData?.fans ?? 0);
  const [week, setWeek]         = useState(gameData?.week ?? 1);
  const [season, setSeason]     = useState(gameData?.season ?? 1);
  const [logistics, setLogistics] = useState(gameData?.logistics || { arena:1, crew:1, sfx:1, advertising:1 });
  const [owned, setOwned]       = useState(gameData?.logisticsOwned || { arena:[1], crew:[1], sfx:[1], advertising:[1] });
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  // ── Rivalries / Championships / flow flags ─────────────────────────
  const [rivalries, setRivalries] = useState(gameData?.rivalries || []);
  const [pastRivalries, setPastRivalries] = useState(gameData?.pastRivalries || []);
  const [champions, setChampions] = useState(gameData?.champions || { slots: [], champions: {} });
  const [endOfSeasonFlag, setEndOfSeasonFlag] = useState(!!gameData?._endOfSeason);
  const [pendingChampionsFlag, setPendingChampionsFlag] = useState(!!gameData?._pendingSetChampions);

  // ── Confirmation 2 etapes (Danger Zone + actions destructives) ─────
  const [confirmAction, setConfirmAction] = useState(null); // { label, sub, run }
  const [pickingChampionFor, setPickingChampionFor] = useState(null); // slot key

  if (accessStatus !== 'admin') return null;

  const toggleOwned = (cat, rank) => {
    if (rank === 1) return;
    const cur = owned[cat] || [1];
    const next = cur.includes(rank) ? cur.filter(r => r !== rank) : [...cur, rank].sort((a,b)=>a-b);
    if (!next.includes(1)) next.unshift(1);
    setOwned(p => ({ ...p, [cat]: next }));
    if (!next.includes(logistics[cat])) {
      setLogistics(p => ({ ...p, [cat]: Math.max(...next.filter(r => r <= logistics[cat])) }));
    }
  };

  // ── Rivalries helpers ────────────────────────────────────────────
  const rivalryLabel = (r) => {
    const s1 = (r.side1Snapshot || []).map(w => w.name).join(' & ') || 'Side 1';
    const s2 = (r.side2Snapshot || []).map(w => w.name).join(' & ') || 'Side 2';
    return s1 + ' vs ' + s2;
  };
  const storylineLabel = (r) => {
    if (!r.storylineId) return null;
    const st = STORYLINES.find(s => s.id === r.storylineId);
    return st ? st.name : null;
  };
  const adjustRivalryLevel = (id, delta) => {
    setRivalries(prev => prev.map(r => r.id === id ? { ...r, level: Math.max(1, Math.min(4, (r.level || 1) + delta)) } : r));
  };
  const endRivalry = (id) => {
    const r = rivalries.find(rv => rv.id === id);
    if (!r) return;
    setConfirmAction({
      label: 'TERMINER CETTE RIVALITE ?',
      sub: rivalryLabel(r) + ' — passera dans l\'historique, action irreversible depuis ce panel.',
      run: () => {
        setRivalries(prev => prev.filter(rv => rv.id !== id));
        setPastRivalries(prev => [{ ...r, ended: true, endWeek: Number(week) }, ...prev]);
      },
    });
  };

  // ── Championships helpers ───────────────────────────────────────
  const titleSlots = champions.slots || [];
  const holderLabel = (key) => {
    const arr = (champions.champions || {})[key];
    if (!arr || arr.length === 0) return 'VACANT';
    return arr.map(w => w.name || w.wrestler_id).join(' & ');
  };
  const vacantTitle = (key, label) => {
    setConfirmAction({
      label: 'VACANT CE TITRE ?',
      sub: label,
      run: () => setChampions(prev => ({ ...prev, champions: { ...(prev.champions || {}), [key]: [] } })),
    });
  };
  const assignChampion = (key, wrestler) => {
    setChampions(prev => ({ ...prev, champions: { ...(prev.champions || {}), [key]: [wrestler] } }));
    setPickingChampionFor(null);
  };

  // ── Flow triggers ────────────────────────────────────────────────
  const forcePLEWeek = () => {
    const cur = Number(week);
    const next = cur % 5 === 0 ? cur : Math.min(50, cur + (5 - (cur % 5)));
    setWeek(next);
  };
  const forceEndOfSeason = () => {
    setConfirmAction({
      label: 'FORCER LA FIN DE SAISON ?',
      sub: 'Week passe a 50, EndOfSeasonFlow se declenchera au prochain chargement.',
      run: () => { setWeek(50); setEndOfSeasonFlag(true); },
    });
  };
  const resetEndOfSeasonFlag = () => {
    setConfirmAction({
      label: 'RESET LE FLAG FIN DE SAISON ?',
      sub: 'A utiliser si un save reste bloque sur l\'ecran de fin de saison.',
      run: () => setEndOfSeasonFlag(false),
    });
  };
  const resetPendingChampions = () => {
    setConfirmAction({
      label: 'RESET LE FLAG CHAMPIONS EN ATTENTE ?',
      sub: 'A utiliser si un save reste bloque sur l\'ecran de set champions.',
      run: () => setPendingChampionsFlag(false),
    });
  };

  const exportJSON = () => {
    const data = {
      ...gameData,
      logisticsTokens: Number(tokens),
      budget: Number(budget),
      fans: Number(fans),
      week: Number(week),
      season: Number(season),
      logistics,
      logisticsOwned: owned,
      rivalries,
      pastRivalries,
      champions,
      _endOfSeason: endOfSeasonFlag,
      _pendingSetChampions: pendingChampionsFlag,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (gameData?.slotName || 'slot') + '_' + Date.now() + '.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!gameData?.id) return;
    setSaving(true);
    const payload = {
      logisticsTokens: Number(tokens),
      budget: Number(budget),
      fans: Number(fans),
      week: Number(week),
      season: Number(season),
      logistics,
      logisticsOwned: owned,
      rivalries,
      pastRivalries,
      champions,
      _endOfSeason: endOfSeasonFlag,
      _pendingSetChampions: pendingChampionsFlag,
    };
    const updated = { ...gameData, ...payload };
    try {
      await updateDoc(doc(db, 'gameSlots', gameData.id), payload);
      onSave && onSave(updated);
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose && onClose(); }, 1200);
    } catch(e) {
      alert('Save error: ' + e.message);
    }
    setSaving(false);
  };

  const gold = '#d4af37';
  const dim = 'rgba(255,255,255,0.12)';
  const faint = 'rgba(255,255,255,0.04)';
  const blue = '#3498db';
  const purple = '#9b59b6';
  const red = '#e74c3c';

  const Field = ({ label, value, onChange, type = 'number', min = 0 }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>{label}</div>
      <input type={type} value={value} min={min}
        onChange={e => onChange(e.target.value)}
        style={{ background: faint, border: '1px solid ' + dim, borderRadius: '5px', color: '#fff', fontFamily: "'Anton',sans-serif", fontSize: '0.75rem', padding: '7px 10px', outline: 'none', width: '100%', letterSpacing: '1px', colorScheme: 'dark' }} />
    </div>
  );

  const SectionHead = ({ label, color }) => (
    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', letterSpacing: '2px', color, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ height: '1px', width: '16px', background: color, opacity: 0.4 }} /> {label} <div style={{ height: '1px', flex: 1, background: color, opacity: 0.1 }} />
    </div>
  );

  const ActionBtn = ({ children, onClick, color = gold }) => (
    <button onClick={onClick}
      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1px', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', textAlign: 'center',
        background: color + '14', border: '1px solid ' + color + '55', color }}>
      {children}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(6,6,6,0.98)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '16px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(212,175,55,0.15)', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, background: 'rgba(6,6,6,0.98)', zIndex: 1 }}>
          <img src={process.env.PUBLIC_URL + '/icons/star-full.png'} alt="" style={{ width: '16px', height: '16px', objectFit: 'contain' }} onError={e => e.target.style.display='none'} />
          <div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '3px', color: gold }}>DEV TOOLS</div>
            {gameData?.slotName && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px', letterSpacing: '1px' }}>{gameData.slotName} · {gameData.brand} · Week {gameData.week}</div>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
            {saved && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: '#2ecc71', letterSpacing: '1px' }}>✓ SAVED</div>}
            <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* General */}
          <div>
            <SectionHead label="GENERAL" color={gold} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              <Field label="Budget" value={budget} onChange={setBudget} />
              <Field label="Fans" value={fans} onChange={setFans} />
              <Field label="Week" value={week} onChange={setWeek} min={1} />
              <Field label="Season" value={season} onChange={setSeason} min={1} />
            </div>
          </div>

          {/* Tokens */}
          <div>
            <SectionHead label="LOGISTICS TOKENS" color={gold} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: faint, border: '1px solid ' + dim, borderRadius: '8px', padding: '6px 10px' }}>
                <button onClick={() => setTokens(t => Math.max(0, Number(t)-1))}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem', width: '24px', lineHeight: 1 }}>−</button>
                <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.6rem', color: gold, minWidth: '36px', textAlign: 'center', letterSpacing: '1px' }}>{tokens}</div>
                <button onClick={() => setTokens(t => Number(t)+1)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem', width: '24px', lineHeight: 1 }}>+</button>
              </div>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {[0,1,2,3,5,10].map(n => (
                  <button key={n} onClick={() => setTokens(n)}
                    style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer',
                      background: Number(tokens)===n ? 'rgba(212,175,55,0.15)' : faint,
                      border: '1px solid ' + (Number(tokens)===n ? 'rgba(212,175,55,0.5)' : dim),
                      color: Number(tokens)===n ? gold : 'rgba(255,255,255,0.35)' }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Logistics */}
          <div>
            <SectionHead label="LOGISTICS RANKS" color={gold} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Object.entries(LOG_CATS).map(([cat, info]) => {
                const active = logistics[cat] || 1;
                const ownedList = owned[cat] || [1];
                return (
                  <div key={cat} style={{ background: faint, border: '1px solid ' + dim, borderRadius: '8px', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', color: '#fff' }}>{info.label}</div>
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: gold, background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '3px', padding: '2px 7px', letterSpacing: '0.5px' }}>
                        {info.names[active]}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {Array.from({ length: info.maxRank }, (_,i) => i+1).map(rank => {
                        const isOwned = ownedList.includes(rank);
                        const isActive = active === rank;
                        const isDefault = rank === 1;
                        return (
                          <div key={rank} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                            <button
                              onClick={() => !isDefault && toggleOwned(cat, rank)}
                              style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', padding: '4px 9px', borderRadius: '4px', cursor: isDefault ? 'default' : 'pointer',
                                background: isOwned ? 'rgba(46,204,113,0.1)' : 'transparent',
                                border: '1px solid ' + (isOwned ? 'rgba(46,204,113,0.45)' : 'rgba(255,255,255,0.08)'),
                                color: isOwned ? '#2ecc71' : 'rgba(255,255,255,0.25)',
                                opacity: isDefault ? 0.6 : 1 }}>
                              R{rank}
                            </button>
                            {isOwned && !isActive && (
                              <button onClick={() => setLogistics(p => ({ ...p, [cat]: rank }))}
                                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.32rem', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer',
                                  background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)', color: 'rgba(212,175,55,0.7)' }}>
                                USE
                              </button>
                            )}
                            {isActive && (
                              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.3rem', color: gold, letterSpacing: '0.5px' }}>●</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Flow triggers */}
          <div>
            <SectionHead label="FLOW TRIGGERS" color={blue} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <ActionBtn color={blue} onClick={forcePLEWeek}>Forcer semaine PLE</ActionBtn>
              <ActionBtn color={blue} onClick={() => { onClose && onClose(); onShowHofSequence && onShowHofSequence(); }}>Relancer HOF sequence</ActionBtn>
            </div>
          </div>

          {/* Rivalries */}
          <div>
            <SectionHead label="RIVALRIES" color={purple} />
            {rivalries.length === 0 && (
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.25)' }}>Aucune rivalite active.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {rivalries.map(r => (
                <div key={r.id} style={{ background: faint, border: '1px solid ' + dim, borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div>
                      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', color: '#fff', letterSpacing: '0.5px' }}>{rivalryLabel(r)}</div>
                      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: purple, letterSpacing: '0.5px', marginTop: '2px' }}>
                        {RIVALRY_LEVEL_LABELS[r.level] || 'BUILDING'}{storylineLabel(r) ? ' · ' + storylineLabel(r) : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button onClick={() => adjustRivalryLevel(r.id, -1)}
                        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.3)', color: purple }}>−</button>
                      <button onClick={() => adjustRivalryLevel(r.id, 1)}
                        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.3)', color: purple }}>+</button>
                      <button onClick={() => endRivalry(r.id)}
                        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.3)', color: red }}>Terminer</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Championships */}
          <div>
            <SectionHead label="CHAMPIONSHIPS" color={gold} />
            {titleSlots.length === 0 && (
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.25)' }}>Aucun titre configure sur ce slot.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {titleSlots.map(s => (
                <div key={s.key} style={{ background: faint, border: '1px solid ' + dim, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.58rem', color: '#fff', letterSpacing: '0.5px' }}>{(s.titleOverride || s.title)?.name || s.label}</div>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: holderLabel(s.key) === 'VACANT' ? red : 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{holderLabel(s.key)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={() => setPickingChampionFor(s.key)}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)', color: gold }}>Assigner</button>
                    <button onClick={() => vacantTitle(s.key, (s.titleOverride || s.title)?.name || s.label)}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.3)', color: red }}>Vacant</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Danger zone */}
          <div>
            <SectionHead label="DANGER ZONE" color={red} />
            <div style={{ background: 'rgba(231,76,60,0.04)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '8px', padding: '12px 14px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px', lineHeight: 1.6 }}>
                Actions de reparation pour saves bloques. Confirmation requise avant application.
                {endOfSeasonFlag && <span style={{ color: red }}> · flag fin de saison actif</span>}
                {pendingChampionsFlag && <span style={{ color: red }}> · flag champions en attente actif</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <ActionBtn color={red} onClick={forceEndOfSeason}>Forcer fin de saison</ActionBtn>
                <ActionBtn color={red} onClick={resetEndOfSeasonFlag}>Reset flag fin de saison</ActionBtn>
                <ActionBtn color={red} onClick={resetPendingChampions}>Reset flag champions en attente</ActionBtn>
                <ActionBtn color={gold} onClick={exportJSON}>Exporter JSON</ActionBtn>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(212,175,55,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '8px', position: 'sticky', bottom: 0, background: 'rgba(6,6,6,0.98)' }}>
          <button onClick={onClose}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)' }}>
            CANCEL
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.56rem', letterSpacing: '2px', padding: '8px 24px', borderRadius: '5px', cursor: saving ? 'not-allowed' : 'pointer',
              background: saving ? 'transparent' : 'rgba(212,175,55,0.12)',
              border: '1px solid ' + (saving ? dim : 'rgba(212,175,55,0.5)'),
              color: saving ? 'rgba(255,255,255,0.2)' : gold }}>
            {saving ? 'SAVING...' : saved ? '✓ SAVED' : 'SAVE'}
          </button>
        </div>
      </div>

      {/* Wrestler picker for champion assignment */}
      {pickingChampionFor && (
        <SuperstarPicker
          roster={gameData?.roster || []}
          onSelect={(w) => assignChampion(pickingChampionFor, w)}
          onClose={() => setPickingChampionFor(null)}
        />
      )}

      {/* 2-step confirmation modal (Danger Zone + actions destructives) */}
      {confirmAction && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}
          onClick={() => setConfirmAction(null)}>
          <div style={{ background: 'rgba(10,6,6,0.98)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '12px', padding: '24px', maxWidth: '360px', width: '100%', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.85rem', letterSpacing: '2px', color: red, marginBottom: '10px' }}>{confirmAction.label}</div>
            {confirmAction.sub && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.4)', marginBottom: '18px', lineHeight: 1.6 }}>{confirmAction.sub}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setConfirmAction(null)}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>
                ANNULER
              </button>
              <button onClick={() => { confirmAction.run(); setConfirmAction(null); }}
                style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'rgba(231,76,60,0.15)', border: '1px solid rgba(231,76,60,0.7)', color: red, padding: '8px 18px', borderRadius: '4px', cursor: 'pointer' }}>
                CONFIRMER
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
